import { useMemo, useState } from "react";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { SectionTitle } from "@/components/common/SectionTitle";
import { RedBand } from "@/components/common/RedBand";
import { Loader, EmptyState } from "@/components/common/Loader";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { usePublicContent } from "@/lib/useContent";
import { cn } from "@/lib/utils";

const CATEGORY_ORDER = [
  "Getting Started",
  "Pricing/Packages",
  "Booking",
  "App Setup",
  "Cancellations/Rescheduling",
  "Membership Commitments",
  "Training Expectations",
  "What to Bring",
  "Parent Questions",
  "Mavs Volleyball",
  "Online Training",
  "Billing/Payments",
  "Support",
];

export default function FAQ() { return <PageRenderer pageKey="faq" />; }

export const FAQBody = () => {
  const { data, loading } = usePublicContent("faqs");
  const [active, setActive] = useState("All");

  // Group by category, ordered by canonical list
  const grouped = useMemo(() => {
    const map = {};
    data.forEach((f) => {
      const c = f.category || "Other";
      (map[c] = map[c] || []).push(f);
    });
    const cats = Object.keys(map).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a); const ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    return cats.map((c) => ({ category: c, items: map[c] }));
  }, [data]);

  const availableCats = ["All", ...grouped.map((g) => g.category)];
  const visible = active === "All" ? grouped : grouped.filter((g) => g.category === active);

  return (
    <div data-testid="faq-page">
      {/* hero via PageRenderer */}

      <section id="faq-list" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
          <SectionTitle eyebrow="Help Center" title="Frequently Asked Questions" subtitle="Browse by category or scroll through all answers." />

          {!loading && grouped.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="FAQ categories" data-testid="faq-filter-tabs">
              {availableCats.map((c) => {
                const isActive = active === c;
                return (
                  <button
                    key={c}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(c)}
                    data-testid={`faq-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className={cn(
                      "rounded-full border px-3.5 py-1.5 font-condensed text-xs font-semibold uppercase tracking-[0.1em] transition-colors duration-200 active:scale-[0.98]",
                      isActive ? "border-[hsl(var(--brand-red))] bg-brand text-white" : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
                    )}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-10 space-y-10">
            {loading ? (
              <Loader />
            ) : grouped.length === 0 ? (
              <EmptyState title="No FAQs yet" />
            ) : (
              visible.map((group) => (
                <div key={group.category} data-testid="faq-category-group">
                  <h3 className="font-condensed text-xl font-semibold uppercase tracking-wide text-brand">{group.category}</h3>
                  <Accordion type="single" collapsible className="mt-3">
                    {group.items.map((f) => (
                      <AccordionItem key={f.id} value={f.id} data-testid="faq-item">
                        <AccordionTrigger className="text-left text-base font-semibold">{f.question}</AccordionTrigger>
                        <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <div id="faq-cta" className="scroll-mt-24">
      <RedBand
        eyebrow="Still Need Help?"
        title="We've Got Your Back"
        text="Can't find your answer? Reach out and our team will help you out."
        primary={{ label: "Contact Support", to: "/contact" }}
        secondary={{ label: "Book a Session", to: "/booking" }}
        testid="faq-cta"
      />
      </div>
    </div>
  );
}
